import { useMutation, useQuery } from '@apollo/client'
import { NoteAlt } from '@mui/icons-material'
import {
    Autocomplete,
    Box,
    IconButton,
    TextField,
    Tooltip,
} from '@mui/material'
import { useGridApiContext } from '@mui/x-data-grid-pro'
import { ProcessStatus, type ProcessStatusGroup } from '__generated__/graphql'
import { type FC, useState } from 'react'
import ProcessStatusPill from 'shared/components/ProcessStatusPill'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { UpdateProcessStatus } from 'recruiter/graphql/updateProcessStatus.gql'
import useToast from 'shared/components/Toast/useToast'
import {
    formatGraphQLError,
    sortProcessStatusesBySummaryGroup,
} from 'shared/utils'

interface ReferenceStatusInlineEditCellProps {
    refRosterId: string
    candidateId: string
    rosterCandidateId?: string
    currentStatus?: ProcessStatus
    currentStatusLabel?: string
    currentSummaryGroup?: ProcessStatusGroup
    currentSummaryGroupLabel?: string
    canEdit: boolean
    rowId: string
    onOpenNoteDrawer: (rosterCandidateId: string) => void
}

const ReferenceStatusInlineEditCell: FC<ReferenceStatusInlineEditCellProps> = ({
    refRosterId,
    candidateId,
    rosterCandidateId,
    currentStatus,
    currentStatusLabel,
    currentSummaryGroup,
    currentSummaryGroupLabel,
    canEdit,
    rowId,
    onOpenNoteDrawer,
}) => {
    const [editing, setEditing] = useState(false)
    const toast = useToast()
    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)
    const gridApi = useGridApiContext()

    const sortedOptions = sortProcessStatusesBySummaryGroup(
        processStatusOptions?.processStatuses ?? []
    )
    const currentOption = currentStatus
        ? sortedOptions.find((o) => o.status === currentStatus)
        : undefined

    const [updateProcessStatus] = useMutation(UpdateProcessStatus, {
        refetchQueries: [GetCandidatesForRosterSql],
        onCompleted(data) {
            if (data.updateCandidateProcessStatus.success) {
                toast({
                    title: 'Success',
                    message:
                        data.updateCandidateProcessStatus.message ||
                        'Reference status updated successfully',
                    severity: 'success',
                })
            } else {
                toast({
                    title: 'Error',
                    message:
                        data.updateCandidateProcessStatus.message ||
                        'Error updating reference status',
                    severity: 'error',
                })
            }
        },
        onError(error) {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error updating reference status'
                ),
                severity: 'error',
            })
        },
    })

    if (!editing) {
        return (
            <Box
                onDoubleClick={() => {
                    if (canEdit) setEditing(true)
                }}
                sx={{
                    cursor: canEdit ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                }}
            >
                <Box sx={{ minWidth: 0, flexShrink: 1 }}>
                    <ProcessStatusPill
                        status={currentStatus}
                        statusLabel={currentStatusLabel ?? 'N/A'}
                        summaryGroup={currentSummaryGroup}
                        summaryGroupLabel={currentSummaryGroupLabel}
                    />
                </Box>
                {rosterCandidateId && (
                    <Tooltip title="Notes">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation()
                                onOpenNoteDrawer(rosterCandidateId)
                            }}
                            sx={{ ml: 'auto', p: 0.25, flexShrink: 0 }}
                        >
                            <NoteAlt sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        )
    }

    return (
        <Autocomplete
            open
            openOnFocus
            onClose={() => setEditing(false)}
            onBlur={() => setEditing(false)}
            onChange={(_, processStatus) => {
                setEditing(false)

                updateProcessStatus({
                    variables: {
                        rosterId: refRosterId,
                        candidateIds: [candidateId],
                        processStatus:
                            processStatus?.status ?? ProcessStatus.NoStatus,
                    },
                    onCompleted: () => {
                        const currentRowId = rowId?.toString()
                        if (currentRowId && gridApi?.current?.getRow) {
                            const existingRow: any =
                                gridApi.current.getRow(currentRowId)
                            if (existingRow) {
                                const existing =
                                    existingRow.referenceRosterCandidates ?? []
                                const newEntry = {
                                    rosterId: refRosterId,
                                    rosterCandidateId: rosterCandidateId ?? '',
                                    processStatus:
                                        processStatus?.status ??
                                        ProcessStatus.NoStatus,
                                    processStatusLabel:
                                        processStatus?.statusLabel ??
                                        'No Status',
                                    summaryGroup:
                                        processStatus?.processStatusSummaryGroup
                                            ?.summaryGroup ?? null,
                                    summaryGroupLabel:
                                        processStatus?.processStatusSummaryGroup
                                            ?.label ?? null,
                                }
                                const hasExisting = existing.some(
                                    (rrc: any) => rrc.rosterId === refRosterId
                                )
                                const updatedRefCandidates = hasExisting
                                    ? existing.map((rrc: any) =>
                                          rrc.rosterId === refRosterId
                                              ? { ...rrc, ...newEntry }
                                              : rrc
                                      )
                                    : [...existing, newEntry]
                                gridApi.current.updateRows([
                                    {
                                        ...existingRow,
                                        referenceRosterCandidates:
                                            updatedRefCandidates,
                                    },
                                ])
                            }
                        }
                    },
                })
            }}
            options={sortedOptions}
            value={currentOption ?? undefined}
            getOptionLabel={(option) => option.statusLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            groupBy={(option) => option.processStatusSummaryGroup?.label || ''}
            disableClearable
            fullWidth
            size="small"
            sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                '& .MuiOutlinedInput-root': {
                    fontSize: '0.75rem',
                    py: 0,
                    '& fieldset': { borderColor: 'divider' },
                    '&:hover fieldset': { borderColor: 'text.secondary' },
                },
            }}
            renderOption={({ key, ...optionProps }, option, { selected }) => (
                <li
                    key={key}
                    {...optionProps}
                    style={{
                        fontSize: '0.75rem',
                        paddingTop: 4,
                        paddingBottom: 4,
                        minHeight: 'unset',
                        backgroundColor: selected
                            ? 'rgba(25, 118, 210, 0.08)'
                            : undefined,
                        borderLeft: selected ? '3px solid #1976d2' : 'none',
                    }}
                >
                    {option.statusLabel}
                </li>
            )}
            slotProps={{
                paper: {
                    sx: {
                        '& .MuiAutocomplete-groupLabel': {
                            fontSize: '0.6875rem',
                            lineHeight: 2,
                            fontWeight: 600,
                        },
                    },
                },
            }}
            renderInput={(renderParams) => (
                <TextField {...renderParams} fullWidth autoFocus />
            )}
        />
    )
}

export default ReferenceStatusInlineEditCell
